import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { Pencil, Trash2, Plus, X, Handshake } from "lucide-react";

const KINDS = [
  { v: "rete_stabile", label: "Rete stabile" },
  { v: "interlocutore_istituzionale", label: "Interlocutore istituzionale" },
  { v: "patrocinio", label: "Patrocinio" },
  { v: "collaborazione_occasionale", label: "Collaborazione occasionale" },
  { v: "partner_progetto", label: "Partner di progetto" },
];
const STATUSES = [
  { v: "attiva", label: "Attiva" },
  { v: "conclusa", label: "Conclusa" },
];

const empty = {
  name: "", logo: "", url: "", description: "",
  kind: "collaborazione_occasionale", status: "attiva",
  year_started: "", year_ended: "", linked_projects: [], featured: false, order: 0,
};

export default function PartnersAdmin() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const [p, pr] = await Promise.all([api.get("/partners"), api.get("/projects")]);
    setItems(p.data); setProjects(pr.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name) return toast.error("Nome obbligatorio");
    const payload = {
      ...editing,
      year_started: editing.year_started ? parseInt(editing.year_started) : null,
      year_ended: editing.year_ended ? parseInt(editing.year_ended) : null,
      order: parseInt(editing.order) || 0,
    };
    try {
      if (editing.id) await api.put(`/admin/partners/${editing.id}`, payload);
      else await api.post("/admin/partners", payload);
      toast.success("Salvato"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const del = async (id) => {
    if (!confirm("Eliminare?")) return;
    await api.delete(`/admin/partners/${id}`);
    toast.success("Eliminato"); load();
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((s) => ({ ...s, logo: data.path }));
    } catch { toast.error("Upload fallito"); }
    finally { setUploading(false); }
  };

  const toggleProject = (pid) => {
    const cur = editing.linked_projects || [];
    setEditing({ ...editing, linked_projects: cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid] });
  };

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    if (filter === "attiva" || filter === "conclusa") return items.filter((p) => p.status === filter);
    return items.filter((p) => p.kind === filter);
  }, [items, filter]);

  const input = "w-full rounded-xl bg-white border border-[#292522]/12 px-3 py-2 focus:border-[#FF6100] focus:ring-2 focus:ring-[#FF6100]/20 outline-none text-sm";

  return (
    <div data-testid="admin-partners">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl font-medium">Reti e collaborazioni</h1>
          <p className="text-sm text-[#292522]/60 mt-1">Partner classificati per tipologia e stato, con progetti collegati.</p>
        </div>
        <button onClick={() => setEditing({ ...empty })} className="btn-primary text-sm" data-testid="partners-new"><Plus className="h-4 w-4" />Nuovo</button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <Chip active={filter === "all"} onClick={() => setFilter("all")}>Tutti</Chip>
        <Chip active={filter === "attiva"} onClick={() => setFilter("attiva")}>Attivi</Chip>
        <Chip active={filter === "conclusa"} onClick={() => setFilter("conclusa")}>Conclusi</Chip>
        <span className="w-px bg-[#292522]/10 mx-1" />
        {KINDS.map((k) => <Chip key={k.v} active={filter === k.v} onClick={() => setFilter(k.v)}>{k.label}</Chip>)}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((p) => (
          <div key={p.id} className="card-vv p-5 bg-white flex flex-col gap-3" data-testid={`partner-card-${p.name}`}>
            <div className="flex items-start gap-3">
              {p.logo ? <img src={fileUrl(p.logo)} alt={p.name} className="h-14 w-14 object-contain bg-[#FFF1E8] rounded p-1" /> : <div className="h-14 w-14 rounded bg-[#FFF1E8] flex items-center justify-center text-[#FF6100]"><Handshake className="h-5 w-5" /></div>}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{p.name}</div>
                <div className="text-xs text-[#292522]/60">{(KINDS.find(k => k.v === p.kind) || {}).label || "—"}</div>
                {p.year_started && <div className="text-xs text-[#292522]/50 mt-0.5">{p.year_started}{p.year_ended && p.year_ended !== p.year_started ? `–${p.year_ended}` : ""}</div>}
              </div>
              <span className={`text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5 ${p.status === "conclusa" ? "bg-[#F8F1E8] text-[#292522]/60" : "bg-[#DDEEE8] text-[#216B65]"}`}>{p.status}</span>
            </div>
            <div className="text-xs text-[#292522]/60">
              {p.linked_projects?.length > 0 ? `${p.linked_projects.length} progetto/i collegato/i` : "Nessun progetto collegato"}
            </div>
            <div className="flex justify-end gap-1 pt-1 border-t border-[#292522]/8 mt-auto">
              <button onClick={() => setEditing({ ...empty, ...p })} className="p-1.5 hover:text-[#FF6100]"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => del(p.id)} className="p-1.5 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="col-span-full rounded-2xl bg-[#F8F1E8] p-10 text-center text-sm text-[#292522]/50">Nessun partner</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-[#292522]/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[#292522]/8 sticky top-0 bg-white">
              <h2 className="font-display text-2xl font-medium">{editing.id ? "Modifica" : "Nuovo"} partner</h2>
              <button onClick={() => setEditing(null)}><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <label className="block"><span className="text-xs font-semibold">Nome *</span><input className={input + " mt-1"} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></label>
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block"><span className="text-xs font-semibold">Tipologia</span>
                  <select className={input + " mt-1"} value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })}>
                    {KINDS.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}
                  </select>
                </label>
                <label className="block"><span className="text-xs font-semibold">Stato</span>
                  <select className={input + " mt-1"} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                    {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                  </select>
                </label>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <label className="block"><span className="text-xs font-semibold">Anno inizio</span><input type="number" className={input + " mt-1"} value={editing.year_started || ""} onChange={(e) => setEditing({ ...editing, year_started: e.target.value })} placeholder="es. 2024" /></label>
                <label className="block"><span className="text-xs font-semibold">Anno fine</span><input type="number" className={input + " mt-1"} value={editing.year_ended || ""} onChange={(e) => setEditing({ ...editing, year_ended: e.target.value })} placeholder="opzionale" /></label>
                <label className="block"><span className="text-xs font-semibold">Ordinamento</span><input type="number" className={input + " mt-1"} value={editing.order || 0} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></label>
              </div>
              <label className="block"><span className="text-xs font-semibold">Sito web</span><input className={input + " mt-1"} value={editing.url || ""} onChange={(e) => setEditing({ ...editing, url: e.target.value })} placeholder="https://…" /></label>
              <label className="block"><span className="text-xs font-semibold">Descrizione</span><textarea rows="3" className={input + " mt-1"} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></label>
              <label className="block"><span className="text-xs font-semibold">Logo</span>
                <input type="file" accept="image/*" onChange={(e) => upload(e.target.files[0])} disabled={uploading} className="text-xs mt-1 block" />
                {editing.logo && <p className="text-xs text-[#216B65] mt-1">✓ {editing.logo}</p>}
              </label>
              <div>
                <div className="text-xs font-semibold mb-2">Progetti collegati</div>
                <div className="flex flex-wrap gap-2">
                  {projects.map((pr) => (
                    <button key={pr.id} type="button" onClick={() => toggleProject(pr.id)} className={`px-3 py-1.5 rounded-full text-xs ${(editing.linked_projects || []).includes(pr.id) ? "bg-[#FF6100] text-white" : "bg-[#F8F1E8] text-[#292522]"}`}>
                      {pr.title}
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> In evidenza
              </label>
            </div>
            <div className="p-5 border-t flex justify-end gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setEditing(null)} className="btn-ghost text-sm">Annulla</button>
              <button onClick={save} className="btn-primary text-sm" data-testid="partner-save">Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`px-3 py-1.5 rounded-full text-xs font-medium ${active ? "bg-[#FF6100] text-white" : "bg-[#F8F1E8] text-[#292522] hover:bg-[#FFF1E8]"}`}>
      {children}
    </button>
  );
}
